import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileDown, MessageSquare, RefreshCw } from "lucide-react";
import { exportToExcel, exportAoa } from "@/lib/excel";
import { tableToPdf, whatsappSend } from "@/lib/pdf";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from "recharts";

const REPORTS = [
  { k: "profit-loss", label: "P&L Summary", cols: ["net_sales", "net_purchase", "expenses", "gross_profit", "net_profit"] },
  { k: "profit-loss/bill-wise", label: "P&L Bill-wise" },
  { k: "profit-loss/item-wise", label: "P&L Item-wise" },
  { k: "gstr1", label: "GSTR-1", plan: "professional" },
  { k: "gstr3b", label: "GSTR-3B", plan: "professional" },
  { k: "balance-sheet", label: "Balance Sheet", plan: "professional" },
  { k: "cash-flow", label: "Cash Flow" },
  { k: "daybook", label: "Daybook" },
  { k: "all-transactions", label: "All Transactions" },
  { k: "trial-balance", label: "Trial Balance" },
  { k: "stock-summary", label: "Stock Summary" },
  { k: "item-wise-discount", label: "Item-wise Discount" },
  { k: "expense-report", label: "Expense Report" },
  { k: "party-wise-pnl", label: "Party-wise P&L" },
  { k: "sale-purchase-by-party", label: "Sale/Purchase by Party" },
  { k: "aging?direction=receivable", label: "Aging — Receivables", plan: "professional" },
  { k: "aging?direction=payable", label: "Aging — Payables", plan: "professional" },
  { k: "overdue", label: "Overdue Bills", plan: "professional" },
];

const PLAN_RANK = { basic: 1, professional: 2, business: 3 };
const canAccess = (report, plan) => (PLAN_RANK[plan] || 0) >= (PLAN_RANK[report.plan || "basic"] || 0);

function exportBalanceSheetScheduleIII(d, co) {
  const r2 = (n) => Math.round((+n || 0) * 100) / 100;
  const a = d.assets, l = d.liabilities, e = d.equity;
  const cash = r2(a.cash_in_hand + a.bank_balances);
  const shf = r2(e.owner_equity + e.retained_earnings);
  const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  const aoa = [
    [co?.name || ""],
    [co?.address || ""],
    [co?.gstin ? `GSTIN: ${co.gstin}` : ""],
    [`Balance Sheet as at ${today}`],
    ["(Schedule III to the Companies Act, 2013 — Division I)", "", "Amount in ₹"],
    [],
    ["Particulars", "Note No.", "Amount (₹)"],
    ["I. EQUITY AND LIABILITIES", "", ""],
    ["(1) Shareholders' Funds", "", ""],
    ["    (a) Share Capital / Owner's Capital", "1", r2(e.owner_equity)],
    ["    (b) Reserves and Surplus (Profit & Loss balance)", "2", r2(e.retained_earnings)],
    ["    (c) Money received against share warrants", "", 0],
    ["", "Sub-total", shf],
    ["(2) Share application money pending allotment", "", 0],
    ["(3) Non-Current Liabilities", "", ""],
    ["    (a) Long-term Borrowings", "3", r2(l.loans_outstanding)],
    ["    (b) Deferred Tax Liabilities (Net)", "", 0],
    ["    (c) Other Long-term Liabilities", "", 0],
    ["    (d) Long-term Provisions", "", 0],
    ["", "Sub-total", r2(l.loans_outstanding)],
    ["(4) Current Liabilities", "", ""],
    ["    (a) Short-term Borrowings", "", 0],
    ["    (b) Trade Payables", "4", r2(l.accounts_payable)],
    ["    (c) Other Current Liabilities", "", 0],
    ["    (d) Short-term Provisions", "", 0],
    ["", "Sub-total", r2(l.accounts_payable)],
    ["TOTAL", "", r2(d.total_liabilities_and_equity)],
    [],
    ["II. ASSETS", "", ""],
    ["(1) Non-Current Assets", "", ""],
    ["    (a) Property, Plant & Equipment and Intangible Assets", "", 0],
    ["    (b) Non-current Investments", "", 0],
    ["    (c) Deferred Tax Assets (Net)", "", 0],
    ["    (d) Long-term Loans and Advances", "", 0],
    ["    (e) Other Non-current Assets", "", 0],
    ["", "Sub-total", 0],
    ["(2) Current Assets", "", ""],
    ["    (a) Current Investments", "", 0],
    ["    (b) Inventories", "5", r2(a.closing_stock)],
    ["    (c) Trade Receivables", "6", r2(a.accounts_receivable)],
    ["    (d) Cash and Cash Equivalents", "7", cash],
    ["    (e) Short-term Loans and Advances", "", 0],
    ["    (f) Other Current Assets", "", 0],
    ["", "Sub-total", r2(a.closing_stock + a.accounts_receivable + cash)],
    ["TOTAL", "", r2(d.total_assets)],
    [],
    ["Notes to Accounts", "", ""],
    ["Note 7 — Cash and Cash Equivalents", "", ""],
    ["    Cash in hand", "", r2(a.cash_in_hand)],
    ["    Balances with banks", "", r2(a.bank_balances)],
    ["", "Total", cash],
    [],
    ["For " + (co?.name || ""), "", ""],
    ["Authorised Signatory", "", ""],
  ];
  exportAoa(aoa, "Balance_Sheet_Schedule_III.xlsx", "Balance Sheet", {
    cols: [62, 12, 18],
    merges: [0, 1, 2, 3].map((r) => ({ s: { r, c: 0 }, e: { r, c: 2 } })),
  });
}

export default function Reports() {
  const [sel, setSel] = useState(REPORTS[0]);
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ frm: "", to: "" });
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("professional");

  useEffect(() => {
    (async () => {
      try {
        const { data: me } = await api.get("/firms/me");
        setPlan(me.plan || "professional");
      } catch {}
    })();
  }, []);

  const load = async () => {
    if (!canAccess(sel, plan)) {
      setData({ __locked: true, required: sel.plan });
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (range.frm) params.set("frm", range.frm);
      if (range.to) params.set("to", range.to);
      const sep = sel.k.includes("?") ? "&" : "?";
      const { data } = await api.get(`/reports/${sel.k}${sep}${params}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 402) {
        setData({ __locked: true, required: sel.plan || "professional" });
      } else {
        toast.error("Failed to load");
      }
    }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sel, plan]);

  const exportExcel = async () => {
    if (sel.k === "balance-sheet" && data?.assets) {
      let co = null; try { co = (await api.get("/companies/current")).data; } catch {}
      exportBalanceSheetScheduleIII(data, co);
      return;
    }
    let rows = [];
    if (data?.rows) rows = data.rows;
    else if (data?.entries) rows = data.entries;
    else if (data && Array.isArray(data)) rows = data;
    else if (data && typeof data === "object") rows = [data];
    exportToExcel(rows, `${sel.k.replace(/[^a-z0-9]+/gi, "_")}.xlsx`);
  };

  const exportPdf = () => {
    let rows = [];
    if (data?.rows) rows = data.rows;
    else if (data?.entries) rows = data.entries;
    else if (data && Array.isArray(data)) rows = data;
    if (!rows.length && data && typeof data === "object") {
      // key-value report (balance sheet, p&l summary) — flatten
      const flat = [];
      const walk = (obj, prefix = "") => {
        for (const [k, v] of Object.entries(obj)) {
          if (v && typeof v === "object" && !Array.isArray(v)) walk(v, prefix + k + ".");
          else flat.push({ key: prefix + k, value: v });
        }
      };
      walk(data);
      rows = flat;
      tableToPdf({
        title: sel.label, subtitle: `${range.frm || "—"} to ${range.to || "—"}`,
        columns: [{ key: "key", label: "Metric", w: 200 }, { key: "value", label: "Value", num: true, w: 200 }],
        rows, filename: `${sel.k.replace(/[^a-z0-9]+/gi, "_")}.pdf`,
      });
      return;
    }
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const cols = Object.keys(rows[0]).slice(0, 8).map((c) => ({
      key: c, label: c, num: typeof rows[0][c] === "number", w: 80,
    }));
    tableToPdf({ title: sel.label, subtitle: `${rows.length} rows`, columns: cols, rows, filename: `${sel.k.replace(/[^a-z0-9]+/gi, "_")}.pdf` });
  };

  const shareWA = () => {
    const summary = data?.total_sales !== undefined
      ? `AccountifyPro P&L: Net Sales ₹${data.net_sales} · Net Profit ₹${data.net_profit}`
      : `AccountifyPro Report: ${sel.label} — see attachment.`;
    whatsappSend("", summary);
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <div className="overline">Reports</div>
          <h1 className="text-3xl font-bold tracking-tight">All Reports</h1>
        </div>
        <div className="flex gap-2 no-print">
          <Input type="date" value={range.frm} onChange={(e) => setRange({ ...range, frm: e.target.value })} className="w-40" />
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="w-40" />
          <Button variant="outline" onClick={load}><RefreshCw size={14} /> Apply</Button>
          <Button variant="outline" onClick={exportExcel} data-testid="report-export"><FileDown size={14} /> Excel</Button>
          <Button variant="outline" onClick={exportPdf} data-testid="report-pdf"><FileDown size={14} /> PDF</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="report-print"><FileDown size={14} /> Print</Button>
          <Button variant="outline" onClick={shareWA} data-testid="report-share"><MessageSquare size={14} /> WhatsApp</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-4 no-print">
        {REPORTS.map((r) => {
          const locked = !canAccess(r, plan);
          return (
            <button key={r.k} onClick={() => setSel(r)} data-testid={`report-${r.k.replace(/[^a-z]+/gi, "-")}`}
              className={`text-xs uppercase tracking-wider p-2 rounded border text-left relative ${sel.k === r.k ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-border bg-white text-slate-700 hover:bg-slate-50"} ${locked ? "opacity-60" : ""}`}>
              {locked && <span className="absolute right-1 top-1 text-[9px]" title={`Requires ${r.plan}`}>🔒</span>}
              {r.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-border rounded-lg p-4 print-area">
        {/* Print sub-heading (shown only when printing) */}
        <div className="hidden print:block mb-4 pb-3 border-b-2 border-slate-900">
          <div className="text-xs uppercase tracking-widest text-slate-500">Report</div>
          <div className="text-2xl font-bold">{sel.label}</div>
          <div className="text-xs text-slate-600">
            Period: {range.frm || "All"} to {range.to || "Today"} · Printed on {new Date().toLocaleString()}
          </div>
        </div>
        {busy && <div className="text-sm">Loading…</div>}
        {!busy && data && <RenderReport k={sel.k} data={data} />}
        {!busy && !data && <div className="text-sm text-muted-foreground p-6 text-center">No data available. Try adjusting the date range or record some transactions first.</div>}
      </div>
    </div>
  );
}

function RenderReport({ k, data }) {
  if (data?.__locked) {
    return (
      <div className="text-center p-10 border border-amber-200 bg-amber-50 rounded-md" data-testid="report-locked">
        <div className="text-4xl mb-2">🔒</div>
        <div className="font-bold text-lg text-amber-900">Locked on your plan</div>
        <div className="text-sm text-amber-800 mt-2 max-w-md mx-auto">
          This report requires the <b className="uppercase">{data.required || "Professional"}</b> plan.
          Upgrade to unlock GSTR filings, aging analysis, balance sheet, and overdue tracking.
        </div>
        <a href="/pricing" className="inline-block mt-4 bg-[#064E3B] text-white px-5 py-2 rounded-md font-semibold text-sm">Upgrade Plan →</a>
      </div>
    );
  }
  // Null-guard: don't crash if backend response is empty/missing keys
  if (!data || typeof data !== "object") return <div className="text-sm text-muted-foreground p-6 text-center">No data.</div>;
  const numFmt = (n) => (typeof n === "number" ? fmtINR(n) : n);
  // Aging report with bar chart
  if (k.startsWith("aging")) {
    const buckets = data.buckets || {};
    const chartData = ["0-30", "31-60", "61-90", "90+"].map((b) => ({ bucket: b, amount: buckets[b] || 0 }));
    const colors = ["#166534", "#65A30D", "#B45309", "#991B1B"];
    return (
      <div>
        <div className="grid md:grid-cols-4 gap-3 mb-4">
          {chartData.map((row, i) => (
            <div key={row.bucket} className="border border-border rounded-md p-3">
              <div className="overline">{row.bucket} days</div>
              <div className="text-xl font-mono font-bold mt-1" style={{ color: colors[i] }}>{fmtINR(row.amount)}</div>
            </div>
          ))}
        </div>
        <div className="border border-border rounded-md p-3 mb-4" style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 30, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
              <XAxis dataKey="bucket" />
              <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtINR(v)} />
              <Legend />
              <Bar dataKey="amount" name={data.direction === "payable" ? "Payable" : "Receivable"}>
                {chartData.map((_, i) => (<Cell key={i} fill={colors[i]} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overline mb-2">Party-wise Aging</div>
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Party</th>
              <th className="num">0-30</th>
              <th className="num">31-60</th>
              <th className="num">61-90</th>
              <th className="num">90+</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {(data.by_party || []).length === 0 && (
              <tr><td colSpan={6} className="text-center py-4 text-muted-foreground">No {data.direction === "payable" ? "payables" : "receivables"} pending.</td></tr>
            )}
            {(data.by_party || []).map((r, i) => (
              <tr key={i} data-testid={`aging-row-${i}`}>
                <td>{r.party_name}</td>
                <td className="num">{fmtINR(r["0-30"])}</td>
                <td className="num">{fmtINR(r["31-60"])}</td>
                <td className="num">{fmtINR(r["61-90"])}</td>
                <td className="num">{fmtINR(r["90+"])}</td>
                <td className="num font-bold">{fmtINR(r.total)}</td>
              </tr>
            ))}
            <tr className="font-bold bg-slate-50">
              <td>Totals</td>
              <td className="num">{fmtINR(buckets["0-30"])}</td>
              <td className="num">{fmtINR(buckets["31-60"])}</td>
              <td className="num">{fmtINR(buckets["61-90"])}</td>
              <td className="num">{fmtINR(buckets["90+"])}</td>
              <td className="num">{fmtINR(data.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  // Overdue Bills report
  if (k === "overdue") {
    return (
      <div>
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="border border-border rounded-md p-3">
            <div className="overline">Overdue Count</div>
            <div className="text-2xl font-mono font-bold text-[#B45309]">{data.count || 0}</div>
          </div>
          <div className="border border-border rounded-md p-3">
            <div className="overline">Receivables Overdue</div>
            <div className="text-2xl font-mono font-bold text-[#166534]">{fmtINR(data.receivable_total)}</div>
          </div>
          <div className="border border-border rounded-md p-3">
            <div className="overline">Payables Overdue</div>
            <div className="text-2xl font-mono font-bold text-[#991B1B]">{fmtINR(data.payable_total)}</div>
          </div>
        </div>
        <table className="acc-table w-full">
          <thead>
            <tr>
              <th>Party</th><th>Invoice</th><th>Date</th><th>Direction</th>
              <th className="num">Balance</th><th className="num">Credit</th><th className="num">Overdue</th>
            </tr>
          </thead>
          <tbody>
            {(data.bills || []).length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-muted-foreground">All caught up — no overdue bills.</td></tr>
            )}
            {(data.bills || []).map((b) => (
              <tr key={b._id_str} data-testid={`report-overdue-${b._id_str}`}>
                <td className="font-semibold">{b.party_name}</td>
                <td className="font-mono">{b.number}</td>
                <td>{new Date(b.date).toLocaleDateString()}</td>
                <td>
                  <span className={`chip ${b.direction === "receivable" ? "chip-green" : "chip-red"} uppercase`}>
                    {b.direction}
                  </span>
                </td>
                <td className="num">{fmtINR(b.balance)}</td>
                <td className="num">{b.credit_period_days}d</td>
                <td className="num text-[#B45309] font-bold">{b.days_overdue}d</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (k === "balance-sheet") {
    if (!data.assets || !data.liabilities) return <div className="text-sm text-muted-foreground p-6 text-center">Balance sheet unavailable.</div>;
    const equity = data.equity || { retained_earnings: 0, owner_equity: 0, total_equity: data.equity || 0 };
    return (
    <div>
      <div className="mb-4 pb-2 border-b border-slate-300 hidden print:block">
        <div className="text-xs text-slate-500">Balance Sheet — as of {new Date().toLocaleDateString()}</div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="overline">Assets</div>
          <table className="acc-table w-full">
            <tbody>
              {Object.entries(data.assets).map(([kk, v]) => <tr key={kk}><td>{kk.replace(/_/g, " ")}</td><td className="num">{fmtINR(v)}</td></tr>)}
              <tr className="font-bold border-t-2"><td>Total Assets</td><td className="num">{fmtINR(data.total_assets)}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <div className="overline">Liabilities & Equity</div>
          <table className="acc-table w-full">
            <tbody>
              {Object.entries(data.liabilities).map(([kk, v]) => <tr key={kk}><td>{kk.replace(/_/g, " ")}</td><td className="num">{fmtINR(v)}</td></tr>)}
              <tr className="font-semibold"><td>Total Liabilities</td><td className="num">{fmtINR(data.total_liabilities)}</td></tr>
              {typeof equity === "object" && Object.entries(equity).filter(([kk]) => kk !== "total_equity").map(([kk, v]) => <tr key={kk}><td>{kk.replace(/_/g, " ")}</td><td className="num">{fmtINR(v)}</td></tr>)}
              <tr className="font-semibold"><td>Total Equity</td><td className="num">{fmtINR(typeof equity === "object" ? equity.total_equity : equity)}</td></tr>
              <tr className="font-bold text-[#064E3B] border-t-2"><td>Total Liab. + Equity</td><td className="num">{fmtINR(data.total_liabilities_and_equity ?? ((data.total_liabilities || 0) + (typeof equity === "object" ? equity.total_equity : equity)))}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      {data.note && <div className="mt-3 text-xs text-slate-500 text-center italic">{data.note}</div>}
    </div>
  ); }
  if (k === "profit-loss") return (
    <table className="acc-table w-full max-w-lg">
      <tbody>
        <tr><td>Net Sales</td><td className="num">{fmtINR(data.net_sales)}</td></tr>
        <tr><td>Net Purchase</td><td className="num">{fmtINR(data.net_purchase)}</td></tr>
        <tr><td>Sales Returns</td><td className="num">{fmtINR(data.sales_returns)}</td></tr>
        <tr><td>Purchase Returns</td><td className="num">{fmtINR(data.purchase_returns)}</td></tr>
        <tr><td>Expenses</td><td className="num">{fmtINR(data.expenses)}</td></tr>
        <tr className="font-semibold"><td>Gross Profit</td><td className="num">{fmtINR(data.gross_profit)}</td></tr>
        <tr className="font-bold text-[#064E3B]"><td>Net Profit</td><td className="num">{fmtINR(data.net_profit)}</td></tr>
      </tbody>
    </table>
  );
  if (k === "gstr1" || k === "gstr3b") {
    if (k === "gstr3b") {
      const outward = data.outward || {};
      const inward = data.inward_itc || {};
      const net = data.net_payable || {};
      return (
      <div>
        {data.formula && <div className="mb-3 text-xs bg-emerald-50 border border-emerald-200 rounded p-2 font-semibold text-emerald-900">{data.formula}</div>}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="overline">Outward Supplies (Sales)</div>
            <table className="acc-table w-full">
              <tbody>
                <tr><td>Taxable Value</td><td className="num">{fmtINR(outward.taxable || 0)}</td></tr>
                <tr><td>CGST</td><td className="num">{fmtINR(outward.cgst || 0)}</td></tr>
                <tr><td>SGST</td><td className="num">{fmtINR(outward.sgst || 0)}</td></tr>
                <tr><td>IGST</td><td className="num">{fmtINR(outward.igst || 0)}</td></tr>
                <tr className="font-bold border-t-2"><td>Total Tax Outward</td><td className="num">{fmtINR(outward.total_tax || 0)}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div className="overline">Input Tax Credit (Purchases)</div>
            <table className="acc-table w-full">
              <tbody>
                <tr><td>Taxable Value</td><td className="num">{fmtINR(inward.taxable || 0)}</td></tr>
                <tr><td>CGST</td><td className="num">{fmtINR(inward.cgst || 0)}</td></tr>
                <tr><td>SGST</td><td className="num">{fmtINR(inward.sgst || 0)}</td></tr>
                <tr><td>IGST</td><td className="num">{fmtINR(inward.igst || 0)}</td></tr>
                <tr className="font-bold border-t-2"><td>Total ITC Available</td><td className="num">{fmtINR(inward.total_itc || 0)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-4 border-2 border-[#064E3B] rounded p-3 bg-emerald-50">
          <div className="overline">Net Tax Payable</div>
          <table className="acc-table w-full">
            <tbody>
              <tr><td>CGST payable</td><td className="num">{fmtINR(net.cgst || 0)}</td></tr>
              <tr><td>SGST payable</td><td className="num">{fmtINR(net.sgst || 0)}</td></tr>
              <tr><td>IGST payable</td><td className="num">{fmtINR(net.igst || 0)}</td></tr>
              <tr className="font-bold text-lg text-[#064E3B]"><td>Total Payable</td><td className="num">{fmtINR(net.total || 0)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      );
    }
    if (!Array.isArray(data.rows)) return <div className="text-sm text-muted-foreground p-6 text-center">GSTR-1 not available yet.</div>;
    return (
      <>
        {data.rows.length === 0 && <div className="text-center text-muted-foreground py-6">No sales in the selected period.</div>}
        {data.rows.length > 0 && (
        <table className="acc-table w-full">
          <thead><tr><th>Date</th><th>Number</th><th>Party</th><th>GSTIN</th><th>Type</th><th className="num">Taxable</th><th className="num">CGST</th><th className="num">SGST</th><th className="num">IGST</th><th className="num">Total</th></tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i}><td>{new Date(r.date).toLocaleDateString()}</td><td className="font-mono">{r.number}</td><td>{r.party}</td><td className="font-mono text-xs">{r.gstin || "URP"}</td><td><span className={`chip ${r.type === "credit_note" ? "chip-red" : "chip-slate"}`}>{r.type}</span></td><td className="num">{fmtINR(r.taxable)}</td><td className="num">{fmtINR(r.cgst)}</td><td className="num">{fmtINR(r.sgst)}</td><td className="num">{fmtINR(r.igst)}</td><td className="num">{fmtINR(r.total)}</td></tr>
            ))}
            <tr className="font-bold bg-slate-50"><td colSpan={5}>Totals</td><td className="num">{fmtINR(data.totals?.taxable)}</td><td className="num">{fmtINR(data.totals?.cgst)}</td><td className="num">{fmtINR(data.totals?.sgst)}</td><td className="num">{fmtINR(data.totals?.igst)}</td><td className="num">{fmtINR(data.totals?.invoice_value)}</td></tr>
          </tbody>
        </table>
        )}
      </>
    );
  }
  if (k === "cash-flow") {
    const entries = Array.isArray(data.entries) ? data.entries : [];
    return (
    <>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="border border-border rounded-md p-3"><div className="overline">In</div><div className="text-2xl font-mono font-bold text-[#166534]">{fmtINR(data.in || 0)}</div></div>
        <div className="border border-border rounded-md p-3"><div className="overline">Out</div><div className="text-2xl font-mono font-bold text-[#991B1B]">{fmtINR(data.out || 0)}</div></div>
        <div className="border border-border rounded-md p-3"><div className="overline">Net</div><div className="text-2xl font-mono font-bold text-[#064E3B]">{fmtINR(data.net || 0)}</div></div>
      </div>
      {entries.length === 0 ? (
        <div className="text-center text-muted-foreground py-6">No cash-flow entries in the selected range.</div>
      ) : (
      <table className="acc-table w-full">
        <thead><tr><th>Date</th><th>Flow</th><th>Type</th><th>Number</th><th>Party</th><th className="num">Amount</th></tr></thead>
        <tbody>
          {entries.map((t, i) => (
            <tr key={(t._id_str || i) + "-" + (t.flow || "")}><td>{t.date ? new Date(t.date).toLocaleDateString() : "—"}</td><td className="uppercase text-xs">{t.flow}</td><td>{t.txn_type}</td><td className="font-mono">{t.number || "—"}</td><td>{t.party_name || "—"}</td><td className={`num ${t.flow === "in" ? "text-[#166534]" : "text-[#991B1B]"}`}>{fmtINR(t.paid || t.total || 0)}</td></tr>
          ))}
        </tbody>
      </table>
      )}
    </>
    );
  }
  if (data?.rows) {
    if (data.rows.length === 0) return <div className="text-center text-muted-foreground py-6">No data yet. Try adjusting filters or record transactions first.</div>;
    const cols = Object.keys(data.rows[0] || {});
    return (
      <table className="acc-table w-full">
        <thead><tr>{cols.map((c) => <th key={c} className={typeof data.rows[0][c] === "number" ? "num" : ""}>{c}</th>)}</tr></thead>
        <tbody>
          {data.rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => <td key={c} className={typeof r[c] === "number" ? "num" : ""}>{typeof r[c] === "number" ? fmtINR(r[c]) : r[c]}</td>)}</tr>
          ))}
          {data.total_value !== undefined && <tr className="font-bold"><td colSpan={cols.length - 1}>Total value</td><td className="num">{fmtINR(data.total_value)}</td></tr>}
          {data.total !== undefined && <tr className="font-bold"><td colSpan={cols.length - 1}>Total</td><td className="num">{fmtINR(data.total)}</td></tr>}
        </tbody>
      </table>
    );
  }
  if (data?.entries) {
    if (data.entries.length === 0) return <div className="text-center text-muted-foreground py-6">No entries yet in the selected range.</div>;
    return (
      <table className="acc-table w-full">
        <thead><tr><th>Date</th><th>Type</th><th>Number</th><th>Party</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Balance</th></tr></thead>
        <tbody>
          {data.entries.map((t) => (
            <tr key={t._id_str}><td>{new Date(t.date).toLocaleDateString()}</td><td className="uppercase text-xs">{t.txn_type}</td><td className="font-mono">{t.number}</td><td>{t.party_name || "—"}</td><td className="num">{fmtINR(t.total)}</td><td className="num">{fmtINR(t.paid)}</td><td className="num">{fmtINR(t.balance)}</td></tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (k === "trial-balance") return (
    <table className="acc-table w-full max-w-2xl">
      <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
      <tbody>
        {data.entries.map((e, i) => <tr key={i}><td>{e.account}</td><td className="num">{fmtINR(e.debit)}</td><td className="num">{fmtINR(e.credit)}</td></tr>)}
        <tr className="font-bold"><td>Totals</td><td className="num">{fmtINR(data.total_debit)}</td><td className="num">{fmtINR(data.total_credit)}</td></tr>
      </tbody>
    </table>
  );
  if (Array.isArray(data)) {
    return (
      <table className="acc-table w-full">
        <thead><tr><th>Date</th><th>Type</th><th>Number</th><th>Party</th><th className="num">Total</th></tr></thead>
        <tbody>
          {data.map((t) => (
            <tr key={t._id_str}><td>{new Date(t.date).toLocaleDateString()}</td><td className="uppercase text-xs">{t.txn_type}</td><td className="font-mono">{t.number}</td><td>{t.party_name || "—"}</td><td className="num">{fmtINR(t.total)}</td></tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
}
